import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tr,
} from '@chakra-ui/react';
import { inquiryApi } from '@texas/api/endpoints/inquiryApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SelectedCell {
  inquiryId: number;
  tierId: number;
  quantity: number;
  currentPrice: number | null;
  label: string;
}

interface Props {
  selectedCells: SelectedCell[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function SetTierPriceModal({
  selectedCells,
  isOpen,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [priceValue, setPriceValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { request } = useApiRequest(inquiryApi.updateTierPrice);

  async function handleSetPrice() {
    setIsLoading(true);
    const price = priceValue.trim() === '' ? null : parseFloat(priceValue);
    for (const cell of selectedCells) {
      await request(cell.inquiryId, cell.tierId, price);
    }
    setIsLoading(false);
    onSaved();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('inquiry.setTierPrice')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            <Flex direction="column" gap={1}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.400">
                {t('inquiry.affectedInquiries')}
              </Text>
              <TableContainer>
                <Table variant="configuration" size="sm">
                  <Tbody>
                    {selectedCells.map((cell) => (
                      <Tr key={`${cell.inquiryId}-${cell.tierId}`}>
                        <Td>
                          <Text fontSize="xs">{cell.label}</Text>
                        </Td>
                        <Td isNumeric={true}>
                          <Text fontSize="xs" color="gray.400">
                            {cell.currentPrice ?? '—'}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Flex>
            <Flex direction="column" gap={1}>
              <Input
                type="number"
                placeholder="—"
                value={priceValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPriceValue(e.target.value)
                }
                size="sm"
              />
              <Text fontSize="xs" color="gray.500">
                {t('inquiry.clearsPriceNote')}
              </Text>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('general.cancel')}
          </Button>
          <Button
            variant="texas-solid"
            size="sm"
            isLoading={isLoading}
            onClick={handleSetPrice}
          >
            {t('inquiry.setTierPrice')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
